import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    CheckCircle2,
    Mail,
    Car,
    Loader2,
} from "lucide-react";
import { CONTACT, FORMULAS, TIME_SLOTS, VEHICLE_TYPES } from "../data/content";
import { WhatsAppIcon } from "../components/WhatsAppIcon";

dayjs.locale("fr");

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = ["Formule", "Créneau", "Véhicule", "Adresse", "Coordonnées", "Confirmation"];

const inputCls =
    "w-full rounded-xl border border-white/10 bg-noir px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-neon/60";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
            {label}
        </span>
        {children}
    </label>
);

export default function Reservation() {
    const [params] = useSearchParams();
    const initial = FORMULAS.some((f) => f.id === params.get("formule"))
        ? params.get("formule")
        : null;

    const [step, setStep] = useState(0);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);
    const [sending, setSending] = useState(false);
    const [serverRef, setServerRef] = useState(null);
    const [occupes, setOccupes] = useState([]);
    const [booking, setBooking] = useState({
        formule: initial,
        date: null,
        slot: null,
        vehicleType: null,
        marque: "",
        modele: "",
        adresse: "",
        cp: "",
        ville: "",
        infos: "",
        nom: "",
        tel: "",
        email: "",
    });

    const days = useMemo(
        () => Array.from({ length: 14 }, (_, i) => dayjs().add(i + 1, "day")),
        [],
    );
    const formula = FORMULAS.find((f) => f.id === booking.formule);
    const fallbackRef = useMemo(
        () => `CML-${Date.now().toString(36).toUpperCase().slice(-6)}`,
        [done],
    );
    const ref = serverRef || fallbackRef;

    useEffect(() => {
        if (!booking.date) return;
        fetch(`${API}/creneaux?date=${booking.date}`)
            .then((r) => r.json())
            .then((d) => {
                const pris = d.occupes || [];
                setOccupes(pris);
                setBooking((b) => (pris.includes(b.slot) ? { ...b, slot: null } : b));
            })
            .catch(() => setOccupes([]));
    }, [booking.date]);

    const set = (key, value) => {
        setBooking((b) => ({ ...b, [key]: value }));
        setError("");
    };

    const valid = () => {
        switch (step) {
            case 0:
                return !!booking.formule || "Choisissez une formule pour continuer.";
            case 1:
                return booking.date && booking.slot
                    ? true
                    : "Sélectionnez une date et un horaire.";
            case 2:
                return !!booking.vehicleType || "Indiquez le type de véhicule.";
            case 3:
                return booking.adresse.trim() && booking.ville.trim()
                    ? true
                    : "Indiquez l'adresse d'intervention.";
            case 4:
                if (!booking.nom.trim()) return "Indiquez votre nom.";
                if (!booking.tel.trim()) return "Indiquez votre numéro de téléphone.";
                return true;
            default:
                return true;
        }
    };

    const next = () => {
        const v = valid();
        if (v !== true) return setError(v);
        setError("");
        setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const prev = () => {
        setError("");
        setStep((s) => Math.max(s - 1, 0));
    };

    const confirm = async () => {
        setSending(true);
        setError("");
        try {
            const res = await fetch(`${API}/reservations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(booking),
            });
            if (res.status === 409) {
                const data = await res.json().catch(() => ({}));
                setError(
                    data.detail ||
                        "Ce créneau vient d'être réservé. Choisissez un autre horaire.",
                );
                setStep(1);
                setOccupes((o) => (o.includes(booking.slot) ? o : [...o, booking.slot]));
                setBooking((b) => ({ ...b, slot: null }));
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setServerRef(data.ref);
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            setError(
                `Impossible d'envoyer la demande pour le moment. Réessayez, ou contactez-nous directement au ${CONTACT.phoneDisplay}.`,
            );
        } finally {
            setSending(false);
        }
    };

    const summaryText = encodeURIComponent(
        `Bonjour Clean My Life ! Je souhaite réserver :\n• Formule : ${formula?.name} (${formula?.price})\n• Date : ${booking.date} à ${booking.slot}\n• Véhicule : ${booking.vehicleType} ${booking.marque} ${booking.modele}\n• Adresse : ${booking.adresse}, ${booking.cp} ${booking.ville}\n• Nom : ${booking.nom}\n• Tél : ${booking.tel}`,
    );

    return (
        <main className="relative min-h-screen pb-24 pt-28 sm:pt-36" data-testid="reservation-page">
            <div className="halo pointer-events-none absolute left-1/2 top-0 h-[26rem] w-[36rem] -translate-x-1/2 rounded-full opacity-60" />
            <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
                <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-neon/90">
                    Réservation en ligne
                </p>
                <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                    Votre créneau Clean My Life
                </h1>
                <p className="mt-3 text-sm text-slate-400 sm:text-base">
                    Nettoyage automobile à domicile sur Cannes & alentours — en moins de 2 minutes.
                </p>

                {/* Progression */}
                <div className="mt-10" data-testid="reservation-progress">
                    <div className="mb-2 flex justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <span>
                            Étape {step + 1} / {STEP_LABELS.length} — {STEP_LABELS[step]}
                        </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-white/5">
                        <motion.div
                            className="h-full rounded-full bg-neon shadow-[0_0_12px_rgba(0,240,255,0.6)]"
                            animate={{ width: `${((step + 1) / STEP_LABELS.length) * 100}%` }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        />
                    </div>
                </div>

                <div className="card-line glow-soft mt-8 rounded-3xl bg-panel p-6 sm:p-10">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={done ? "done" : step}
                            initial={{ opacity: 0, x: 26 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -26 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        >
                            {!done && step === 0 && (
                                <div data-testid="step-formule">
                                    <h2 className="mb-6 font-display text-xl font-bold text-white">
                                        Choisissez votre formule
                                    </h2>
                                    <div className="grid gap-4 sm:grid-cols-3">
                                        {FORMULAS.map((f) => (
                                            <button
                                                key={f.id}
                                                type="button"
                                                data-testid={`select-formule-${f.id}`}
                                                onClick={() => set("formule", f.id)}
                                                className={`rounded-2xl border p-5 text-left transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 ${
                                                    booking.formule === f.id
                                                        ? "border-neon bg-neon/5 glow-strong"
                                                        : "border-white/10 bg-noir hover:border-neon/40"
                                                }`}
                                            >
                                                <img
                                                    src={f.img}
                                                    alt={`${f.name} — ${f.price}`}
                                                    className="w-full max-w-[150px] object-contain"
                                                />
                                                <span className="mt-3 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                                                    {f.tag}
                                                </span>
                                                <span className="sr-only">
                                                    {f.name} — {f.price}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!done && step === 1 && (
                                <div data-testid="step-creneau">
                                    <h2 className="mb-6 font-display text-xl font-bold text-white">
                                        Choisissez votre créneau
                                    </h2>
                                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                                        Date d'intervention
                                    </p>
                                    <div className="mb-8 grid grid-cols-3 gap-2 sm:grid-cols-5">
                                        {days.map((d) => {
                                            const val = d.format("YYYY-MM-DD");
                                            return (
                                                <button
                                                    key={val}
                                                    type="button"
                                                    data-testid={`date-option-${val}`}
                                                    onClick={() => set("date", val)}
                                                    className={`rounded-xl border px-2 py-3 text-center transition-colors duration-300 ${
                                                        booking.date === val
                                                            ? "border-neon bg-neon/10 text-neon"
                                                            : "border-white/10 bg-noir text-slate-300 hover:border-neon/40"
                                                    }`}
                                                >
                                                    <span className="block text-[11px] uppercase tracking-wider">
                                                        {d.format("ddd")}
                                                    </span>
                                                    <span className="block font-display text-lg font-bold">
                                                        {d.format("D")}
                                                    </span>
                                                    <span className="block text-[11px] uppercase tracking-wider">
                                                        {d.format("MMM")}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                                        Heure de début
                                    </p>
                                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                        {TIME_SLOTS.map((t) => {
                                            const pris = occupes.includes(t);
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    data-testid={`slot-option-${t.replace(":", "")}`}
                                                    onClick={() => set("slot", t)}
                                                    disabled={pris}
                                                    className={`rounded-xl border py-3 font-mono text-sm font-semibold transition-colors duration-300 ${
                                                        pris
                                                            ? "cursor-not-allowed border-white/5 bg-noir text-slate-700 line-through"
                                                            : booking.slot === t
                                                              ? "border-neon bg-neon/10 text-neon"
                                                              : "border-white/10 bg-noir text-slate-300 hover:border-neon/40"
                                                    }`}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {occupes.length > 0 && (
                                        <p className="mt-3 text-xs text-slate-500" data-testid="slots-occupied-note">
                                            Les horaires barrés sont déjà réservés ce jour-là.
                                        </p>
                                    )}
                                </div>
                            )}

                            {!done && step === 2 && (
                                <div data-testid="step-vehicule">
                                    <h2 className="mb-6 font-display text-xl font-bold text-white">
                                        Votre véhicule
                                    </h2>
                                    <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                        {VEHICLE_TYPES.map((v) => (
                                            <button
                                                key={v}
                                                type="button"
                                                data-testid={`vehicle-option-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                onClick={() => set("vehicleType", v)}
                                                className={`flex items-center gap-2 rounded-xl border px-4 py-3.5 text-sm font-medium transition-colors duration-300 ${
                                                    booking.vehicleType === v
                                                        ? "border-neon bg-neon/10 text-neon"
                                                        : "border-white/10 bg-noir text-slate-300 hover:border-neon/40"
                                                }`}
                                            >
                                                <Car size={16} className="shrink-0" />
                                                {v}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <Field label="Marque">
                                            <input
                                                data-testid="vehicle-marque-input"
                                                className={inputCls}
                                                placeholder="Ex. Porsche, BMW, Renault…"
                                                value={booking.marque}
                                                onChange={(e) => set("marque", e.target.value)}
                                            />
                                        </Field>
                                        <Field label="Modèle">
                                            <input
                                                data-testid="vehicle-modele-input"
                                                className={inputCls}
                                                placeholder="Ex. 911, X5, Clio…"
                                                value={booking.modele}
                                                onChange={(e) => set("modele", e.target.value)}
                                            />
                                        </Field>
                                    </div>
                                </div>
                            )}

                            {!done && step === 3 && (
                                <div data-testid="step-adresse">
                                    <h2 className="mb-6 font-display text-xl font-bold text-white">
                                        Adresse d'intervention
                                    </h2>
                                    <div className="grid gap-4">
                                        <Field label="Adresse">
                                            <input
                                                data-testid="address-input"
                                                className={inputCls}
                                                placeholder="Numéro et rue"
                                                value={booking.adresse}
                                                onChange={(e) => set("adresse", e.target.value)}
                                            />
                                        </Field>
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <Field label="Code postal">
                                                <input
                                                    data-testid="address-cp-input"
                                                    className={inputCls}
                                                    placeholder="06400"
                                                    inputMode="numeric"
                                                    value={booking.cp}
                                                    onChange={(e) => set("cp", e.target.value)}
                                                />
                                            </Field>
                                            <Field label="Ville">
                                                <input
                                                    data-testid="address-ville-input"
                                                    className={inputCls}
                                                    placeholder="Cannes"
                                                    value={booking.ville}
                                                    onChange={(e) => set("ville", e.target.value)}
                                                />
                                            </Field>
                                        </div>
                                        <Field label="Informations d'accès (optionnel)">
                                            <textarea
                                                data-testid="address-infos-input"
                                                className={`${inputCls} min-h-24 resize-none`}
                                                placeholder="Code portail, étage, parking…"
                                                value={booking.infos}
                                                onChange={(e) => set("infos", e.target.value)}
                                            />
                                        </Field>
                                    </div>
                                </div>
                            )}

                            {!done && step === 4 && (
                                <div data-testid="step-coordonnees">
                                    <h2 className="mb-6 font-display text-xl font-bold text-white">
                                        Vos coordonnées
                                    </h2>
                                    <div className="grid gap-4">
                                        <Field label="Nom & prénom">
                                            <input
                                                data-testid="contact-nom-input"
                                                className={inputCls}
                                                placeholder="Jean Dupont"
                                                value={booking.nom}
                                                onChange={(e) => set("nom", e.target.value)}
                                            />
                                        </Field>
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <Field label="Téléphone">
                                                <input
                                                    data-testid="contact-tel-input"
                                                    className={inputCls}
                                                    placeholder="06 12 34 56 78"
                                                    inputMode="tel"
                                                    value={booking.tel}
                                                    onChange={(e) => set("tel", e.target.value)}
                                                />
                                            </Field>
                                            <Field label="E-mail (optionnel)">
                                                <input
                                                    data-testid="contact-email-input"
                                                    className={inputCls}
                                                    type="email"
                                                    placeholder="jean@exemple.fr"
                                                    value={booking.email}
                                                    onChange={(e) => set("email", e.target.value)}
                                                />
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!done && step === 5 && (
                                <div data-testid="step-confirmation">
                                    <h2 className="mb-6 font-display text-xl font-bold text-white">
                                        Récapitulatif
                                    </h2>
                                    <dl className="space-y-3 rounded-2xl border border-white/10 bg-noir p-5 text-sm">
                                        {[
                                            ["Formule", `${formula?.name} — ${formula?.price}`],
                                            [
                                                "Créneau",
                                                `${dayjs(booking.date).format("dddd D MMMM YYYY")} à ${booking.slot}`,
                                            ],
                                            [
                                                "Véhicule",
                                                `${booking.vehicleType}${booking.marque ? ` · ${booking.marque}` : ""}${booking.modele ? ` ${booking.modele}` : ""}`,
                                            ],
                                            [
                                                "Adresse",
                                                `${booking.adresse}, ${booking.cp} ${booking.ville}`,
                                            ],
                                            ["Client", `${booking.nom} · ${booking.tel}`],
                                        ].map(([k, v]) => (
                                            <div key={k} className="flex justify-between gap-4">
                                                <dt className="shrink-0 text-slate-500">{k}</dt>
                                                <dd className="text-right font-medium capitalize text-white">
                                                    {v}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                    <p className="mt-4 text-xs leading-relaxed text-slate-500">
                                        En confirmant, votre demande nous est transmise
                                        immédiatement par e-mail. Aucun paiement n'est demandé à
                                        cette étape.
                                    </p>
                                </div>
                            )}

                            {done && (
                                <div className="py-6 text-center" data-testid="booking-success">
                                    <CheckCircle2 size={56} className="mx-auto text-neon" strokeWidth={1.5} />
                                    <h2 className="mt-5 font-display text-2xl font-extrabold text-white">
                                        Demande bien reçue !
                                    </h2>
                                    <p
                                        className="mt-2 font-mono text-sm font-semibold tracking-widest text-neon"
                                        data-testid="booking-ref"
                                    >
                                        Référence {ref}
                                    </p>
                                    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-400">
                                        Votre réservation est enregistrée et nous sommes prévenus
                                        par e-mail. Nous confirmons votre créneau personnellement
                                        très rapidement, par téléphone ou WhatsApp.
                                    </p>
                                    <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                                        <a
                                            href={`${CONTACT.whatsapp}?text=${summaryText}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="send-whatsapp-button"
                                            className="inline-flex items-center justify-center gap-2 rounded-full bg-neon px-7 py-3.5 text-sm font-bold text-noir transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(0,240,255,0.5)]"
                                        >
                                            <WhatsAppIcon size={17} /> Nous écrire sur WhatsApp
                                        </a>
                                        <a
                                            href={`mailto:${CONTACT.email}?subject=Réservation ${ref}&body=${summaryText}`}
                                            data-testid="send-email-button"
                                            className="inline-flex items-center justify-center gap-2 rounded-full border border-neon/40 px-7 py-3.5 text-sm font-semibold text-neon transition-colors duration-300 hover:bg-neon/10"
                                        >
                                            <Mail size={17} /> Nous écrire par e-mail
                                        </a>
                                    </div>
                                    <Link
                                        to="/"
                                        data-testid="back-home-link"
                                        className="mt-8 inline-block text-sm text-slate-500 transition-colors hover:text-neon"
                                    >
                                        ← Retour à l'accueil
                                    </Link>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>

                    {!done && (
                        <>
                            {error && (
                                <p
                                    data-testid="step-error"
                                    className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                                >
                                    {error}
                                </p>
                            )}
                            <div className="mt-8 flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    data-testid="step-prev-button"
                                    onClick={prev}
                                    disabled={step === 0}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:border-neon/40 hover:text-neon disabled:cursor-not-allowed disabled:opacity-30"
                                >
                                    <ArrowLeft size={16} /> Retour
                                </button>
                                {step < STEP_LABELS.length - 1 ? (
                                    <button
                                        type="button"
                                        data-testid="step-next-button"
                                        onClick={next}
                                        className="inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3 text-sm font-bold text-noir transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_26px_rgba(0,240,255,0.45)]"
                                    >
                                        Continuer <ArrowRight size={16} />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        data-testid="confirm-booking-button"
                                        onClick={confirm}
                                        disabled={sending}
                                        className="inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3 text-sm font-bold text-noir transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_26px_rgba(0,240,255,0.45)] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        {sending ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" /> Envoi…
                                            </>
                                        ) : (
                                            <>
                                                <Check size={16} /> Confirmer la demande
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
